import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { useAppSettings, resolveImageUrl } from "../context/AppSettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, KeyRound, Eye, EyeOff, ArrowRight, ChevronLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Landing pointed to by the "reset password" email/WhatsApp link.
 * Verifies the token by calling `/public/password/reset/complete` and, on
 * success, stores the returned session and redirects to `/carta/:card`.
 */
export default function ResetPassword() {
  const { token } = useParams();
  const nav = useNavigate();
  const { settings } = useAppSettings();
  const logo = resolveImageUrl(settings.logo_url);
  // Pulsanti area riservata forzati BLU + BIANCO
  const brand = "#1E40AF";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (password.length < 6) return toast.error("Password minima 6 caratteri");
    if (password !== confirm) return toast.error("Le password non coincidono");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/public/password/reset/complete`, {
        token, password,
      });
      try {
        localStorage.setItem(`otp_session_${data.card_number}`, JSON.stringify({
          session_token: data.session_token, card_number: data.card_number,
          expires_at: data.expires_at,
        }));
      } catch {}
      toast.success("Password aggiornata");
      nav(`/carta/${encodeURIComponent(data.card_number)}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Link non valido o scaduto");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="reset-password">
      <Toaster position="top-center" richColors closeButton />
      <header className="sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-zinc-100">
        <div className="max-w-md mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/cliente" className="h-10 w-10 rounded-2xl ring-1 ring-zinc-100 flex items-center justify-center hover:bg-red-50" style={{ color: brand }}>
            <ChevronLeft className="h-5 w-5" />
          </Link>
          {logo ? (
            <img src={logo} alt={settings.app_name} className="h-9 object-contain" />
          ) : (
            <span className="font-heading font-extrabold" style={{ color: brand }}>{settings.app_name}</span>
          )}
          <span className="h-10 w-10" />
        </div>
      </header>
      <main className="max-w-md mx-auto px-6 py-8 space-y-5">
        <div className="text-center">
          <div className="mx-auto h-14 w-14 rounded-2xl flex items-center justify-center mb-4"
               style={{ background: `${brand}18`, color: brand }}>
            <KeyRound className="h-7 w-7" />
          </div>
          <h1 className="font-heading font-extrabold text-2xl">Nuova password</h1>
          <p className="text-sm text-zinc-500 mt-1">Scegli la password per la tua area riservata.</p>
        </div>

        <div className="space-y-3">
          <Label className="text-zinc-700 text-sm">Password (min. 6)</Label>
          <div className="relative">
            <Lock className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="reset-password-input"
              type={showPass ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12 bg-white border-zinc-300 text-lg pl-9 pr-9"
              autoFocus
            />
            <button type="button" onClick={() => setShowPass((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700">
              {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>

          <Label className="text-zinc-700 text-sm">Ripeti password</Label>
          <div className="relative">
            <Lock className="h-4 w-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input
              data-testid="reset-password-confirm"
              type={showPass ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              className="h-12 bg-white border-zinc-300 text-lg pl-9"
            />
          </div>
          {password && confirm && password !== confirm && (
            <div className="text-[11px] text-red-500" data-testid="reset-password-mismatch">Le password non coincidono</div>
          )}

          <Button
            data-testid="reset-password-submit"
            onClick={submit}
            disabled={busy || password.length < 6 || password !== confirm}
            className="w-full h-12 text-white font-semibold text-base"
            style={{ background: brand }}
          >
            {busy ? "Salvataggio…" : "Salva e accedi"} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </main>
    </div>
  );
}
